import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { API_CONFIG } from '../constants/api';
import toast from 'react-hot-toast';

interface DrumSeparationData {
    Kick: string;
    Snare: string;
    HiHat: string;
    Tom: string;
    Cymbal: string;
}

interface StemSeparationData {
    Bass: string;
    Keys: string;
    Wind: string;
    Drums: string;
    Piano: string;
    Vocals: string;
    Guitars: string;
    Strings: string;
}

interface ChordsTranscriptionData {
    chords: string;
}

interface WebSocketEvent {
    code: 'derivative.audio' | 'derivative.stems' | 'soundworld.preview' | 'soundworld.stems' | 'error' | 'audio.mastering' | 'audio.transcribe_chords' | 'audio.stem_separation' | 'audio.drum_stems';
    message?: string;
    error?: string;
    job_id?: string;
    project_id?: string;
    piece_id?: string;
    url?: string;
    project_type?: string;
    session_id?: string;
    output?: string | DrumSeparationData | StemSeparationData | ChordsTranscriptionData;
    chords?: string;
    name?: string;
}

interface WebSocketState {
    isConnected: boolean;
    isConnecting: boolean;
    error: string | null;
}

// Shared singleton state to ensure only one WebSocket connection across the app
type SharedWsState = {
    socket: WebSocket | null;
    isConnecting: boolean;
    isConnected: boolean;
    subscribers: number;
};

const shared: SharedWsState = (globalThis as any).__DEL_PHOS_WS_SHARED__ || {
    socket: null,
    isConnecting: false,
    isConnected: false,
    subscribers: 0,
};

(globalThis as any).__DEL_PHOS_WS_SHARED__ = shared;

export const useWebSocket = () => {
    const { getWebSocketUrl, isAuthenticated, client_id, session_id, refreshAuthState, createSession } = useAuth();
    const [state, setState] = useState<WebSocketState>({
        isConnected: false,
        isConnecting: false,
        error: null
    });
    const socketRef = useRef<WebSocket | null>(shared.socket);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const isConnectingRef = useRef(false);

    const isWebSocketDisabled = () => {
        return localStorage.getItem('websocket_disabled') === 'true';
    };

    const createNewSession = useCallback(async () => {
        try {
            if (!client_id || !localStorage.getItem('access_token')) {
                throw new Error('Missing authentication tokens');
            }

            localStorage.removeItem("session_id");
            const access_token = localStorage.getItem('access_token');
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.NEW_SESSION}`, {
                method: 'GET',
                headers: {
                    'Authorization': `${client_id}:${access_token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to create new session');
            }

            const { session_id: newSessionId } = await response.json();

            localStorage.setItem("session_id", newSessionId);

            return newSessionId;
        } catch (error) {
            console.error('Failed to create new session:', error);
            throw error;
        }
    }, [client_id]);

    const connect = useCallback(async () => {

        if (shared.isConnecting || shared.isConnected || socketRef.current?.readyState === WebSocket.CONNECTING || state.isConnected) {
            console.log('WebSocket connect blocked - already connecting or connected');
            return;
        }

        if (isWebSocketDisabled()) {
            return;
        }

        if (!isAuthenticated) {
            const errorMessage = 'Not authenticated';
            setState(prev => ({ ...prev, error: errorMessage, isConnecting: false }));
            toast.error(errorMessage);
            return;
        }

        if (!client_id) {
            const errorMessage = 'Missing client_id';
            setState(prev => ({ ...prev, error: errorMessage }));
            toast.error(errorMessage);
            return;
        }

        try {
            // Create a new session only if absent
            if (!localStorage.getItem('session_id')) {
                await createNewSession();
            }

            const currentSessionId = localStorage.getItem('session_id');
            if (!currentSessionId) {
                throw new Error('Failed to get session ID after creating new session');
            }

            const wsProtocol = API_CONFIG.BASE_URL.startsWith('https') ? 'wss' : 'ws';
            const baseUrl = API_CONFIG.BASE_URL.replace('https://', '').replace('http://', '');
            const path = `${API_CONFIG.ENDPOINTS.EVENTS_WS}/${client_id}/${currentSessionId}`;
            const wsUrl = new URL(`${wsProtocol}://${baseUrl}${path}`).href;

            isConnectingRef.current = true;
            shared.isConnecting = true;
            setState(prev => ({ ...prev, isConnecting: true, error: null }));

            const socket = new WebSocket(wsUrl);
            socketRef.current = socket;
            shared.socket = socket;

            socket.onopen = () => {
                isConnectingRef.current = false;
                shared.isConnecting = false;
                setState(prev => ({
                    ...prev,
                    isConnected: true,
                    isConnecting: false,
                    error: null
                }));
                shared.isConnected = true;
                reconnectAttemptsRef.current = 0;
            };

            socket.onmessage = (event) => {
                try {
                    if (event.data === "ping") {
                        localStorage.setItem('last_ws_ping', Date.now().toString());
                        return;
                    }
                    const data: WebSocketEvent = JSON.parse(event.data);

                    if (socket.readyState === WebSocket.OPEN) {
                        socket.send(JSON.stringify("OK"));
                    }

                    handleWebSocketEvent(data);
                } catch (error) {
                    const errorMessage = 'Failed to parse WebSocket message';
                    console.error(errorMessage, error);
                    toast.error(errorMessage);
                }
            };

            socket.onerror = (error) => {
                isConnectingRef.current = false;
                shared.isConnecting = false;
                const errorMessage = 'WebSocket connection error';
                setState(prev => ({
                    ...prev,
                    error: errorMessage,
                    isConnecting: false
                }));
                toast.error(errorMessage);
            };

            socket.onclose = (event) => {
                isConnectingRef.current = false;
                shared.isConnecting = false;
                setState(prev => ({
                    ...prev,
                    isConnected: false,
                    isConnecting: false
                }));
                shared.isConnected = false;

                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                }

                if (event.code === 1000) {
                    return;
                }

                if (!isWebSocketDisabled()) {
                    reconnectAttemptsRef.current++;
                    const delay = Math.min(2000 * Math.pow(2, reconnectAttemptsRef.current - 1), 60000);

                    reconnectTimeoutRef.current = setTimeout(() => {
                        connect().catch(error => {
                            console.error('Failed to reconnect WebSocket:', error);
                        });
                    }, delay);
                } else {
                    console.warn('WebSocket is disabled, not attempting to reconnect.');
                }
            };

        } catch (error) {
            isConnectingRef.current = false;
            const errorMessage = 'Failed to create WebSocket connection';
            setState(prev => ({
                ...prev,
                error: errorMessage,
                isConnecting: false
            }));
            toast.error(errorMessage);
        }
    }, [isAuthenticated, client_id, session_id, createNewSession]);

    const disconnect = useCallback(() => {
        if (!socketRef.current && !state.isConnected && !state.isConnecting) {
            return;
        }

        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        isConnectingRef.current = false;
        reconnectAttemptsRef.current = 0;

        if (socketRef.current) {
            socketRef.current.close(1000, 'Manual disconnect');
            socketRef.current = null;
            shared.socket = null;
        }

        setState({
            isConnected: false,
            isConnecting: false,
            error: null
        });
    }, [state.isConnected, state.isConnecting]);

    const disableWebSocket = useCallback(() => {
        localStorage.setItem('websocket_disabled', 'true');
        disconnect();
    }, [disconnect]);

    const enableWebSocket = useCallback(() => {
        localStorage.removeItem('websocket_disabled');
        if (isAuthenticated) {
            connect().catch(error => {
                console.error('Failed to enable WebSocket:', error);
                toast.error('Failed to enable WebSocket');
            });
        }
    }, [isAuthenticated, connect]);

    const handleWebSocketEvent = useCallback((event: WebSocketEvent) => {
        switch (event.code) {
            case 'derivative.audio':
                window.dispatchEvent(new CustomEvent('audioReady', {
                    detail: {
                        projectId: event.project_id,
                        pieceId: event.piece_id,
                        url: event.url,
                        projectType: event.project_type
                    }
                }));
                break;

            case 'derivative.stems':
                window.dispatchEvent(new CustomEvent('stemsReady', {
                    detail: {
                        projectId: event.project_id,
                        pieceId: event.piece_id,
                        url: event.url
                    }
                }));
                break;

            case 'soundworld.preview':
                window.dispatchEvent(new CustomEvent('previewReady', {
                    detail: {
                        pieceId: event.piece_id,
                        url: event.url
                    }
                }));
                break;

            case 'soundworld.stems':
                window.dispatchEvent(new CustomEvent('soundworldStemsReady', {
                    detail: {
                        projectId: event.project_id,
                        pieceId: event.piece_id,
                        url: event.url
                    }
                }));
                break;

            case 'audio.mastering':
                window.dispatchEvent(new CustomEvent('audioMasteringReady', {
                    detail: {
                        sessionId: event.session_id,
                        output: event.output
                    }
                }));
                break;

            case 'audio.transcribe_chords':
                const chords_output = event.output as ChordsTranscriptionData;
                window.dispatchEvent(new CustomEvent('audioTranscribeChordsReady', {
                    detail: {
                        sessionId: event.session_id,
                        chords: chords_output?.chords || event.chords,
                        name: event.name
                    }
                }));
                break;

            case 'audio.stem_separation':
                const stem_sep_output = event.output as StemSeparationData;
                window.dispatchEvent(new CustomEvent('audioStemSeparationReady', {
                    detail: {
                        sessionId: event.session_id,
                        name: event.name,
                        Bass: stem_sep_output.Bass,
                        Keys: stem_sep_output.Keys,
                        Wind: stem_sep_output.Wind,
                        Drums: stem_sep_output.Drums,
                        Piano: stem_sep_output.Piano,
                        Vocals: stem_sep_output.Vocals,
                        Guitars: stem_sep_output.Guitars,
                        Strings: stem_sep_output.Strings
                    }
                }));
                break;

            case 'audio.drum_stems':
                const drums_output = event.output as DrumSeparationData;
                window.dispatchEvent(new CustomEvent('audioDrumStemsReady', {
                    detail: {
                        sessionId: event.session_id,
                        name: event.name,
                        Kick: drums_output.Kick,
                        Snare: drums_output.Snare,
                        HiHat: drums_output.HiHat,
                        Tom: drums_output.Tom,
                        Cymbal: drums_output.Cymbal
                    }
                }));
                break;

            case 'error':
                const errorMessage = event.error || event.message || 'Unknown error occurred';

                toast.error(`WebSocket Error: ${errorMessage}`);

                window.dispatchEvent(new CustomEvent('jobError', {
                    detail: {
                        message: errorMessage,
                        projectId: event.project_id,
                        pieceId: event.piece_id
                    }
                }));
                break;
        }
    }, []);

    useEffect(() => {

        if (shared.isConnected && shared.socket && shared.socket.readyState === WebSocket.OPEN) {
            // Adopt shared connection
            socketRef.current = shared.socket;
            setState(prev => ({ ...prev, isConnected: true, isConnecting: false, error: null }));
            return;
        }

        if (isConnectingRef.current || state.isConnecting || state.isConnected || shared.isConnecting) {
            console.log('WebSocket useEffect blocked - already connecting or connected');
            return;
        }

        if (isAuthenticated && client_id && session_id && !isWebSocketDisabled()) {
            const timer = setTimeout(() => {
                if (!isConnectingRef.current && !state.isConnecting && !state.isConnected) {
                    connect().catch(error => {
                        toast.error('Failed to connect WebSocket');
                    });
                } else {
                    console.log('WebSocket useEffect connect blocked - state changed during delay');
                }
            }, 100);

            return () => clearTimeout(timer);
        } else if (!isAuthenticated && !client_id && !session_id) {
            console.log('WebSocket useEffect calling refreshAuthState');
            refreshAuthState();
        }
    }, [isAuthenticated, client_id, session_id, connect, refreshAuthState]);

    useEffect(() => {
        refreshAuthState();
    }, [refreshAuthState]);


    useEffect(() => {
        if (!isAuthenticated && state.isConnected) {
            disconnect();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        shared.subscribers += 1;
        return () => {
            shared.subscribers -= 1;
            // Only disconnect when no more subscribers exist
            if (shared.subscribers <= 0 && !isAuthenticated) {
                disconnect();
            }
        };
    }, [disconnect, isAuthenticated]);

    return {
        ...state,
        session_id,
        connect,
        disconnect,
        disableWebSocket,
        enableWebSocket,
        isDisabled: isWebSocketDisabled(),
        sendMessage: (message: any) => {
            if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                socketRef.current.send(JSON.stringify(message));
            }
        }
    };
};